import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useSubscription } from '../../hooks/useSubscription';
import ArticleCard from './ArticleCard';

export default function ArticlesPage() {
  const { articles, parseArticle, dictLoading, dictError } = useAppContext();
  const { canAddArticle, limits, usedArticles } = useSubscription();
  const navigate = useNavigate();
  const [inputTitle, setInputTitle] = useState('');
  const [inputText, setInputText] = useState('');
  const [showForm, setShowForm] = useState(false);

  const sortedArticles = [...articles].sort((a, b) => b.savedAt - a.savedAt);

  const handleParse = async () => {
    const article = await parseArticle(inputTitle, inputText);
    if (article) {
      setInputTitle('');
      setInputText('');
      setShowForm(false);
      navigate(`/app/articles/${article.id}`);
    }
  };

  return (
    <div className="p-4 pt-14 md:p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Articles</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {usedArticles}/{limits.maxArticles === Infinity ? '∞' : limits.maxArticles} articles this month
          </p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          disabled={!canAddArticle && !showForm}
          className="px-4 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:bg-slate-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
        >
          {showForm ? 'Cancel' : '+ New Article'}
        </button>
      </div>

      {!canAddArticle && (
        <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-400 px-4 py-3 rounded-lg mb-6 text-sm">
          You've reached the limit of {limits.maxArticles} articles per month on your plan.{' '}
          <Link to="/pricing" className="font-medium underline">Upgrade</Link> for more.
        </div>
      )}

      {dictError && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6 text-sm">
          <strong>Dictionary error:</strong> {dictError}
        </div>
      )}

      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 p-6 mb-8">
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-4">Parse a New Article</h2>
          <input
            type="text"
            placeholder="Article title"
            value={inputTitle}
            onChange={e => setInputTitle(e.target.value)}
            className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100"
          />
          <textarea
            placeholder="Paste Japanese text here..."
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            rows={6}
            maxLength={10000}
            className="w-full px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg text-sm mb-1 resize-y focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent leading-relaxed bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100"
          />
          <div className={`text-xs mb-3 ${inputText.length > 9500 ? 'text-amber-500' : 'text-slate-400'}`}>
            {inputText.length.toLocaleString()} / 10,000 characters
          </div>
          <button
            onClick={handleParse}
            disabled={!inputText.trim() || dictLoading}
            className="px-6 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:bg-slate-300 dark:disabled:bg-slate-600 disabled:cursor-not-allowed"
          >
            {dictLoading ? 'Loading...' : 'Parse Article'}
          </button>
        </div>
      )}

      {sortedArticles.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-4xl mb-4">📖</div>
          <h3 className="text-lg font-medium text-slate-600 dark:text-slate-300 mb-2">No articles yet</h3>
          <p className="text-sm text-slate-400">Click "+ New Article" to parse your first Japanese article</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedArticles.map(article => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
      )}
    </div>
  );
}
